import React, { useCallback } from 'react';
import { Select, MenuItem, Checkbox, ListItemText, SelectChangeEvent, Box, Typography } from '@mui/material';
import { useSelector, useDispatch } from 'src/redux/store';
import { selectData, selectSelectedCompanyIds } from 'src/redux/selectors';
import { setSelectedCompanyIds } from 'src/redux/slices/dataSlice';
import { useLocales } from 'src/locales';
import { filterTransfersBySelectedCompanyIds } from 'src/redux/slices/transfersSlice';
import { Data } from 'src/types/data';

const CompanySelect = () => {
  const dispatch = useDispatch();
  const data: Data | null = useSelector(selectData);
  const selectedCompanyIds = useSelector(selectSelectedCompanyIds);
  const { t } = useLocales();

  const handleChange = useCallback(
    (event: SelectChangeEvent<number[]>) => {
      const value = event.target.value as number[];

      if (value.includes(-1)) {
        if (selectedCompanyIds.length !== data?.companies.length) {
          const allCompanyIds = data?.companies.map((company) => company.id) || [];
          dispatch(setSelectedCompanyIds(allCompanyIds));
          dispatch(filterTransfersBySelectedCompanyIds(allCompanyIds));
        }
      } else if (value.length !== 0) {
        dispatch(setSelectedCompanyIds(value));
        dispatch(filterTransfersBySelectedCompanyIds(value));
      } else {
        dispatch(setSelectedCompanyIds([]));
        dispatch(filterTransfersBySelectedCompanyIds([]));
      }
    },
    [data, selectedCompanyIds, dispatch]
  );


  if (!data || data.companies.length === 0) {
    return <Typography>{t('common.topbar.noCompany')}</Typography>;
  }

  return (
    <Box>
      <Select
        multiple
        value={selectedCompanyIds}
        onChange={handleChange}
        renderValue={(selected) => {
          if (selected.length === data.companies.length) {
            return t('common.topbar.allCompanies');
          }
          const selectedNames = data.companies
            .filter((company) => selected.includes(company.id))
            .map((company) => company.name)
            .join(', ');
          return selectedNames;
        }}
        sx={{
          minWidth: 130,
          maxWidth: 200,
          padding: '4px 6px',
          '& .MuiSelect-select': {
            padding: '4px 6px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        }}
      >
        <MenuItem value={-1}>
          <Checkbox checked={selectedCompanyIds.length === data.companies.length} />
          <ListItemText primary={t('common.topbar.all')} />
        </MenuItem>
        {data.companies.map((company) => (
          <MenuItem key={company.id} value={company.id}>
            <Checkbox checked={selectedCompanyIds.includes(company.id)} />
            <ListItemText primary={company.name} />
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

export default CompanySelect;
